import React from 'react';
import { previousRecapDateLabel } from './previousRecapsModel';
import './LastLesson.css';

/**
 * "Last lesson": the newest Lesson Recap for this student, on the page itself
 * (design pass 2, change 2, 2026-09-21). The first ten seconds of a lesson are
 * who, what song, what happened last time, what to work on; the third was a
 * disclosure at the bottom of a drawer under three empty editors.
 *
 * READS WHAT THE PAGE ALREADY HOLDS. `previous` is the page-owned result of
 * usePreviousRecaps, the same read the Previous recaps panel renders; this row
 * is a second presentation of the first entry, never a second query.
 *
 * ANY TIER, LABELLED. Staff read every tier, so the newest lesson shows its
 * first tier in STAFF order (Teachers only, then Everyone, then Parents;
 * Anthony, 2026-09-25) with the tier named. The Everyone-first fallback in the recommendation is the
 * family surface (batch C), which never fetches the private tier at all.
 *
 * ONE TAP: the whole row is the control (44px minimum) and opens the one
 * page-owned RecapDrawer with the Previous recaps disclosure open and in view.
 * Copy carries no em dashes; icons are FontAwesome.
 */
const LastLesson = ({ previous, onOpen }) => {
	const lessons = previous?.lessons || [];
	const isLoading = Boolean(previous?.isLoading);
	const error = previous?.error || '';
	const latest = lessons[0] || null;
	const tier = latest?.tiers?.[0] || null;
	const count = lessons.length;

	let body;
	if (error) {
		body = <p className="ld-lastlesson__text ld-lastlesson__text--empty">{error}</p>;
	} else if (isLoading) {
		body = <p className="ld-lastlesson__text ld-lastlesson__text--empty">Checking earlier lessons</p>;
	} else if (!latest || !tier) {
		body = <p className="ld-lastlesson__text ld-lastlesson__text--empty">No recap yet</p>;
	} else {
		body = (
			<>
				<span className="ld-lastlesson__head">
					<span className="ld-lastlesson__date">{previousRecapDateLabel(latest.date)}</span>
					<span className="ld-lastlesson__teacher">{latest.teacherName}</span>
					<span className={`ld-lastlesson__tier${tier.privateTier ? ' ld-lastlesson__tier--private' : ''}`}>
						<i className={`fa-sharp fa-solid ${tier.icon}`} aria-hidden="true" /> {tier.title}
					</span>
				</span>
				<p className="ld-lastlesson__text">{tier.text}</p>
			</>
		);
	}

	return (
		<button
			type="button"
			className={`ld-lastlesson${latest ? '' : ' ld-lastlesson--empty'}`}
			aria-label="Last lesson: open previous recaps"
			onClick={onOpen}>
			<i className="fa-sharp fa-solid fa-clock-rotate-left ld-lastlesson__icon" aria-hidden="true" />
			<span className="ld-lastlesson__body">
				<span className="ld-lastlesson__eyebrow">Last lesson</span>
				{body}
			</span>
			<span className="ld-lastlesson__more">
				All recaps
				{count > 0 && <span className="ld-lastlesson__count"> · {count}</span>}
				<i className="fa-sharp fa-solid fa-chevron-right" aria-hidden="true" />
			</span>
		</button>
	);
};

export default LastLesson;
